import Address from "../../../classes/Address";
import Sale from "../../../classes/Sale";
import SaleItem from "../../../classes/SaleItem";
import SaleLabor from "../../../classes/SaleLabor";
import SaleMiscItem from "../../../classes/SaleMiscItem";
import { normalizeItemLocationHistory } from "../../../classes/ItemLocation";
import { db, storage } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  increment,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { call, dataURItoFile } from "../../../util/misc";
import { setLoadedCount } from "../../../util/loadedItems";
import { isPickupAddressAtWarehouse } from "../../../util/pickupLocations";
import { forEach, get, isEmpty, set } from "lodash";

// Each report submission uploads its signature / PDF / proof into a folder of
// its own. Uploading over an existing object issues it a new download token,
// which revokes the URL already saved on the document: a re-submit after a
// successful labor report (sale 01100014B) re-uploaded `signature.svg`, the
// call then failed, and the saved signature link started returning 403.
const reportUploadFolder = (folderPath) => `${folderPath}/${Date.now()}`;

const uploadSignature = async (folderPath, signature) => {
  const signatureFile = await dataURItoFile(signature, "signature.svg");
  const signatureRef = storageRef(
    storage,
    `${folderPath}/${signatureFile.name}`
  );
  await uploadBytes(signatureRef, signatureFile);
  return getDownloadURL(signatureRef);
};

export const GET_SALE = async (
  { dispatch },
  { projectID, saleID, forceUpdate = false }
) => {
  let path = Sale.getPath(projectID, saleID);
  return dispatch("GET_DOC", {
    converter: Sale,
    path,
    stateKey: "sales",
    stateID: Sale.getStateID(path),
    ...(forceUpdate ? { forceUpdate: true } : {}),
  });
};

export const GET_SALES = (
  { dispatch },
  { projectID, conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: Sale,
    stateKey: "sales",
    path: projectID ? Sale.getPath(projectID) : null,
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_SALE = async (
  { dispatch },
  { projectID, saleID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Sale,
    path: Sale.getPath(projectID, saleID),
    stateKey: "sales",
    id: saleID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_SALES = (
  { dispatch },
  {
    projectID,
    conditions,
    where,
    pagination,
    listenerPath,
    error,
    next,
    unlisten,
  }
) => {
  const effectiveConditions = conditions ?? where;
  const path = projectID ? Sale.getPath(projectID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: Sale,
    stateKey: "sales",
    path,
    // When no projectID is given (e.g. SaleSelect mounting from
    // EventEdit), fall back to a collection-group query regardless of
    // whether conditions are supplied. The previous gating on
    // `effectiveConditions` left both path and colGroup null and threw
    // "Either 'path' or 'colGroup' must be provided".
    colGroup: conditions || !path ? "sales" : null,
    listenerPath,
    conditions: effectiveConditions,
    pagination,
    next,
    error,
    unlisten,
  });
};

export const CREATE_SALE = async (context, salesLeadID) => {
  return call("createSale", salesLeadID);
};

export const REASSIGN_SALE = async (
  context,
  { projectID, saleID, assignees, teamID }
) => {
  return call("reassignSale", { projectID, saleID, assignees, teamID });
};

export const CHANGE_SALE = async (context, salesLeadID) => {
  return call("changeSale", salesLeadID);
};

export const CANCEL_SALE = async (context, payload) => {
  return call("cancelSale", payload);
};

export const REPAIR_SALE = async (context, { projectID, saleID }) => {
  return call("repairSale", { projectID, saleID });
};

export const REPAIR_SALE_STATUS = async (context, { projectID, saleID }) => {
  return call("repairSaleStatus", { projectID, saleID });
};

export const UPDATE_SALE = ({ state }, { projectID, saleID, changes }) => {
  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  return updateDoc(doc(db, Sale.getPath(projectID, saleID)), changes);
};

export const UPLOAD_SALE_LEGAL_DOCS = async (
  { state, dispatch },
  { sale, docs }
) => {
  const batch = writeBatch(db);
  const changes = {
    modified: new Date(),
    modifiedBy: state.user.id,
  };

  changes.legalDocs = await dispatch("SAVE_DOCUMENTS", {
    batch,
    media: docs,
    folderPath: `${sale.path}/legal`,
    existingData: sale.data,
    fieldPath: "legalDocs",
  });

  batch.update(doc(db, sale.path), changes);

  return batch.commit();
};

export const UPDATE_SALE_STATUS = async (
  { dispatch, state },
  { batch, projectID, saleID, changes }
) => {
  const data = {};
  const sale = new Sale(
    projectID,
    saleID,
    (await dispatch("GET_SALE", { projectID, saleID })).data
  );

  if (changes) {
    Object.assign(data, changes);
    // Apply change to sale
    forEach(changes, (value, path) => {
      set(sale.data, path, value);
    });
  }

  // Update current Sale status
  const status = sale.status.get();

  if (status !== sale.data.status.type) {
    data["status.type"] = status;
  }

  const modified = new Date();
  const modifiedBy = state.user.id;

  // Save changes
  if (!isEmpty(data)) {
    data.modified = modified;
    data.modifiedBy = modifiedBy;
    if (batch) {
      batch.update(doc(db, Sale.getPath(projectID, saleID)), data);
    } else {
      await updateDoc(doc(db, Sale.getPath(projectID, saleID)), data);
    }
  }
  return batch || true;
};

// Sale Items

export const GET_SALE_ITEM = async (
  { commit, state },
  { projectID, saleID, itemID }
) => {
  const path = SaleItem.getPath(projectID, saleID, itemID);
  if (itemID in state.saleItems) {
    return state.saleItems[itemID];
  }
  const snapshot = await getDoc(doc(db, path).withConverter(SaleItem));
  if (!snapshot.exists()) {
    throw "Not found";
  }
  const saleItem = snapshot.data();
  commit("SET", { stateKey: "saleItems", doc: saleItem });
  return saleItem;
};

export const GET_SALE_ITEMS = async (
  { commit, state },
  { projectID, saleID, itemIDs }
) => {
  // Accumulate fetched docs and commit them in a single SET_MANY instead of
  // one SET per item. Each SET mutates the reactive `saleItems` map and forces
  // every consumer (e.g. an event's `items` getter, read per render) to
  // recompute; batching collapses N re-render passes into one. `getDoc` (not a
  // transaction) is used so reads can still be served from the offline cache.
  const docs = [];
  await Promise.all(
    itemIDs.map((itemID) => {
      if (itemID in state.saleItems) {
        return undefined;
      }
      return getDoc(
        doc(db, SaleItem.getPath(projectID, saleID, itemID)).withConverter(
          SaleItem
        )
      ).then((snapshot) => {
        const data = snapshot.data();
        if (data) {
          docs.push({ stateID: snapshot.id, doc: data });
        }
      });
    })
  );

  if (docs.length) {
    commit("SET_MANY", { stateKey: "saleItems", docs });
  }

  return itemIDs.map((id) => state.saleItems[id]);
};

/**
 * Every item of one sale, read once into `saleItems` -- for a flow that needs
 * a sale's items now and holds no listener on them (receiving a carton whose
 * PO is joining the session with it). Resolves to the items.
 */
export const GET_ALL_SALE_ITEMS = ({ dispatch }, { projectID, saleID }) => {
  return dispatch("GET_COLLECTION", {
    converter: SaleItem,
    stateKey: "saleItems",
    path: SaleItem.getPath(projectID, saleID),
  });
};

export const LISTEN_SALE_ITEM = (
  { dispatch },
  { projectID, saleID, itemID, error, next, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: SaleItem,
    path: SaleItem.getPath(projectID, saleID, itemID),
    stateKey: "saleItems",
    id: itemID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_SALE_ITEMS = (
  { dispatch },
  { projectID, saleID, conditions, listenerPath, error, next, unlisten }
) => {
  const path = projectID && saleID ? SaleItem.getPath(projectID, saleID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: SaleItem,
    stateKey: "saleItems",
    path,
    colGroup: conditions || !path ? "items" : null,
    listenerPath,
    conditions,
    next,
    error,
    unlisten,
  });
};

// Listen to just the sale items at one `warehouse:zone` (or, with only
// `warehouse`, anywhere in that warehouse), via the `warehouseZones` /
// `warehouses` arrays touchItem derives — instead of every undelivered item,
// which floods the shared Listen stream. Each call replaces the previous
// listener on `listenerPath`; with no location it just stops listening.
//
// Items the store already holds at the location are dropped first: once
// nothing listens to them they stop updating, so one moved away meanwhile
// would still show here with old locations (and a transfer would write those
// back). The new listener re-adds the ones really there.
export const LISTEN_WAREHOUSE_SALE_ITEMS = async (
  { commit, dispatch, state },
  { listenerPath, warehouse, zone, next, error }
) => {
  await dispatch("UNLISTEN", listenerPath);
  const location = zone || warehouse;
  if (!location) return null;
  const locationOf = (id) =>
    String(id || "")
      .split(":")
      .slice(0, zone ? 2 : 1)
      .join(":");
  const stale = Object.entries(state.saleItems || {})
    .filter(([, item]) =>
      (item?.itemLocation || []).some(
        (l) => locationOf(l.data?.id) === location
      )
    )
    .map(([stateID]) => stateID);
  commit("REMOVE_MANY", { stateKey: "saleItems", stateIDs: stale });
  return dispatch("LISTEN_SALE_ITEMS", {
    conditions: [
      zone ? "warehouseZones" : "warehouses",
      "array-contains",
      location,
    ],
    listenerPath,
    next,
    error,
    unlisten: true,
  });
};

// One-time collection-group fetch of sale items matching a filter. Unlike
// LISTEN_SALE_ITEMS this uses a single get() (a separate RPC) instead of a
// real-time listener, so it does not register a watch across every `items`
// subcollection — which would otherwise head-of-line block the shared Listen
// stream that page listeners rely on. Use for dashboard reads that don't need
// live updates.
export const GET_SALE_ITEMS_WHERE = ({ dispatch }, { where, limit }) => {
  return dispatch("GET_COLLECTION", {
    converter: SaleItem,
    stateKey: "saleItems",
    colGroup: "items",
    where,
    limitCount: limit,
  });
};

export const LISTEN_SALE_MISC_ITEMS = (
  { dispatch },
  { projectID, saleID, conditions, listenerPath, error, next, unlisten }
) => {
  const path =
    projectID && saleID ? SaleMiscItem.getPath(projectID, saleID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: SaleMiscItem,
    stateKey: "miscItems",
    path,
    colGroup: conditions || !path ? "misc" : null,
    listenerPath,
    conditions,
    next,
    error,
    unlisten,
  });
};

export const SAVE_SALE_MISC_ITEM = async (
  { commit, state },
  { projectID, saleID, miscItem }
) => {
  const now = new Date();
  const userId = state.user.id;
  miscItem.data.modified = now;
  miscItem.data.modifiedBy = userId;

  if (!miscItem.id) {
    miscItem.data.created = now;
    miscItem.data.createdBy = userId;
    const ref = doc(collection(db, SaleMiscItem.getPath(projectID, saleID)));
    miscItem.id = ref.id;
    await setDoc(ref.withConverter(SaleMiscItem), miscItem);
  } else {
    await setDoc(
      doc(
        db,
        SaleMiscItem.getPath(projectID, saleID, miscItem.id)
      ).withConverter(SaleMiscItem),
      miscItem,
      { merge: true }
    );
  }

  commit("SET", { stateKey: "miscItems", doc: miscItem });
  return miscItem;
};

export const SAVE_SALE_ITEM = async ({ dispatch, state }, item) => {
  item.data.itemLocations = normalizeItemLocationHistory(
    item.data.itemLocations
  );
  item.data.quantityDelivered = Math.max(
    item.data.quantityDelivered || 0,
    item.data.quantityInstalled || 0
  );

  const { projectID, saleID, id: itemID } = item;
  const path = SaleItem.getPath(projectID, saleID, itemID);
  const oldItem = state.saleItems?.[itemID];
  const oldDelivered = oldItem?.data.quantityDelivered || 0;
  const oldInstalled = oldItem?.data.quantityInstalled || 0;
  const oldQuantity = oldItem?.data.quantity || 0;
  const oldQuantityReady = oldItem?.data.quantityReady || 0;
  const oldQuantityScheduled = oldItem?.data.quantityScheduled || 0;

  if (!itemID) {
    item.data.created = new Date();
    item.data.createdBy = state.user.id;
    const ref = doc(collection(db, SaleItem.getPath(projectID, saleID)));
    item.id = ref.id;
    await setDoc(doc(db, item.path).withConverter(SaleItem), item);
  } else {
    item.data.modified = new Date();
    item.data.modifiedBy = state.user.id;
    await setDoc(doc(db, path).withConverter(SaleItem), item);
  }

  // Update sale status if delivered/installed/quantity/quantityReady changed.
  // Pass BOTH the old item (for the pre-edit bucket/labor classification) and
  // the new buffer (`newItem`) so that an edit which reclassifies the item
  // between the units/screens/hardware buckets — e.g. giving an un-numbered
  // Hardware item a line number — moves its counts between `total.*` buckets
  // instead of stranding them (sales 03900050A / 04500054A stuck in
  // "scheduling"). For a brand-new item, `oldItem` is undefined so both
  // sources are the buffer and the old quantities are all zero.
  await dispatch("UPDATE_SALE_ITEM_STATUS", {
    projectID,
    saleID,
    item: oldItem || item,
    newItem: item,
    oldDelivered,
    oldInstalled,
    oldQuantity,
    oldQuantityReady,
    oldQuantityScheduled,
    newDelivered: item.data.quantityDelivered || 0,
    newInstalled: item.data.quantityInstalled || 0,
    newQuantity: item.data.quantity || 0,
    newQuantityReady: item.data.quantityReady || 0,
    newQuantityScheduled: item.data.quantityScheduled || 0,
  });

  // An edit that makes this item fully ready may complete its vendor, the
  // receipt signal SAVE_PACKING_SLIP otherwise evaluates. Without this, sale
  // 01100014B's original order stayed unreceived after its last short line
  // was set ready here. Raise-only, and a no-op while the vendor is short.
  const newQuantity = item.data.quantity || 0;
  const isFullyReady =
    newQuantity > 0 && (item.data.quantityReady || 0) >= newQuantity;
  const wasFullyReady = oldQuantity > 0 && oldQuantityReady >= oldQuantity;
  if (isFullyReady && !wasFullyReady && item.data.brand) {
    await dispatch("RAISE_VENDOR_RECEIPT", {
      projectID,
      saleID,
      vendor: item.data.brand,
    });
  }

  return item;
};

export const DELETE_SALE_ITEM = async ({ dispatch }, item) => {
  const { projectID, saleID, id: itemID } = item;
  await deleteDoc(doc(db, SaleItem.getPath(projectID, saleID, itemID)));
  await dispatch("UPDATE_SALE_ITEM_STATUS", {
    projectID,
    saleID,
    item,
    oldDelivered: item.data.quantityDelivered || 0,
    oldInstalled: item.data.quantityInstalled || 0,
    oldQuantity: item.data.quantity || 0,
    oldQuantityReady: item.data.quantityReady || 0,
    oldQuantityScheduled: item.data.quantityScheduled || 0,
    newDelivered: 0,
    newInstalled: 0,
    newQuantity: 0,
    newQuantityReady: 0,
    newQuantityScheduled: 0,
  });
};

/**
 * Classify a sale item into the counter bucket it feeds: "accessory", "units",
 * "screens", or "hardware".
 *
 *   • CHILD accessories (any item carrying a `parentID`) feed the dedicated
 *     `total.accessories` / `delivery.accessories*` counters. They are priced
 *     add-ons of a parent unit, but they are physically separate stock that is
 *     received, QC'd, loaded and delivered on its own, so they must be tracked
 *     — they are deliberately kept OUT of `total.items` (a quote-line count
 *     that never includes them) to avoid the ">100% delivered" inflation of
 *     sale 50000030A. A STANDALONE accessory (series "Accessories" but NO
 *     `parentID`) is a quote line of its own and still classifies as "units".
 *   • Only UN-NUMBERED (no `line`) Screens/Hardware feed the dedicated
 *     `total.screens` / `total.hardware` (and `delivery.*Screens/*Hardware`)
 *     counters.
 *   • Everything else — regular units, standalone accessories, numbered/
 *     specialty screens & hardware — feeds the `units` counters
 *     (`total.items`, `delivery.complete`, etc.).
 *
 * Mirrors `classifySaleItem` in `functions/sales-helpers.js` and
 * `getScheduledCounterDelta` in `src/store/main/actions/events.js`, and the
 * `isChildAccessory` exclusion in `scripts/repair-sale-item-totals.mjs`.
 *
 * @param {object} item - a SaleItem instance (or `{ data, isScreens, isHardware }`)
 * @returns {"accessory"|"units"|"screens"|"hardware"}
 */
export function classifyItemBucket(item) {
  if (!item) return "units";
  // A `parentID` is the reliable child-accessory marker (robust even if the
  // child was saved with a "Hardware"/"Screens" series label). Read it from
  // either shape — a SaleItem instance (`item.data`) or the raw item data —
  // for the same reason the series/line reads below tolerate both.
  if (item.parentID ?? item.data?.parentID) return "accessory";
  const hasLine = Boolean(item.line ?? item.data?.line);
  const isHardware = Boolean(
    item.isHardware ?? (item.series ?? item.data?.series) === "Hardware"
  );
  const isScreens = Boolean(
    item.isScreens ?? (item.series ?? item.data?.series) === "Screens"
  );
  if (!hasLine && isHardware) return "hardware";
  if (!hasLine && isScreens) return "screens";
  return "units";
}

/**
 * The sale-level counter contributions a single item makes, given its bucket
 * and its quantities. Diffing NEW contributions against OLD contributions is
 * what lets a reclassified item MOVE its counts between buckets instead of
 * stranding them — the root cause of sales stuck in "scheduling" (03900050A,
 * 04500054A) after an un-numbered Hardware/Screens item was given a line
 * number (becoming a unit) without `total.hardware`/`total.screens` ever being
 * decremented.
 *
 * @param {"accessory"|"units"|"screens"|"hardware"} bucket
 * @param {{delivered:number,installed:number,quantity:number,ready:number,scheduled:number}} q
 * @param {{mirrorToPickup:boolean,hasLabor:boolean}} flags
 * @returns {Object<string,number>} map of sale field path → contribution amount
 */
function bucketContributions(bucket, q, { mirrorToPickup, hasLabor }) {
  const delivered = q.delivered || 0;
  const installed = q.installed || 0;
  const quantity = q.quantity || 0;
  const ready = q.ready || 0;
  const scheduled = q.scheduled || 0;
  const c = {};

  if (bucket === "accessory") {
    // Child accessories are physically separate stock (separately received,
    // QC'd, loaded and delivered), so they carry their own counter triple.
    // They deliberately do NOT feed `total.items` / `delivery.complete`,
    // whose denominator is quote-line based and excludes them — folding them
    // in is what pushed `delivery.ready` past `total.items` on sale
    // 50000030A.
    c["total.accessories"] = quantity;
    c["status.delivery.accessoriesComplete"] = delivered;
    c["status.delivery.accessoriesReady"] = ready;
    c["status.delivery.accessoriesScheduled"] = scheduled;
    if (mirrorToPickup) c["status.pickup.accessoriesReady"] = ready;
    return c;
  }

  if (bucket === "hardware") {
    c["total.hardware"] = quantity;
    c["status.delivery.hardwareComplete"] = delivered;
    c["status.delivery.hardwareReady"] = ready;
    if (mirrorToPickup) c["status.pickup.hardwareReady"] = ready;
  } else if (bucket === "screens") {
    c["total.screens"] = quantity;
    c["status.delivery.screensComplete"] = delivered;
    c["status.delivery.screensReady"] = ready;
    if (mirrorToPickup) c["status.pickup.screensReady"] = ready;
  } else {
    c["total.items"] = quantity;
    c["status.delivery.complete"] = delivered;
    c["status.install.completeItems"] = installed;
    c["status.delivery.ready"] = ready;
    c["status.delivery.scheduled"] = scheduled;
    if (mirrorToPickup) c["status.pickup.ready"] = ready;
    // Labored units also track ready toward install.readyItems.
    if (hasLabor) c["status.install.readyItems"] = ready;
  }

  return c;
}

/**
 * Pure builder for the sale-level counter updates (and per-labor
 * `status.quantityReady` deltas) produced by a sale-item change. Works via a
 * contribution diff so it is correct both when an item stays in its bucket
 * (reduces to the classic per-dimension delta) and when it changes bucket
 * (removes counts from the old bucket, adds them to the new one).
 *
 * @param {object} args
 * @param {"units"|"screens"|"hardware"} args.oldBucket
 * @param {"units"|"screens"|"hardware"} args.newBucket
 * @param {object} args.oldQuantities - {delivered,installed,quantity,ready,scheduled}
 * @param {object} args.newQuantities - {delivered,installed,quantity,ready,scheduled}
 * @param {?string} args.oldLaborId - labor id the item pointed at before (if any)
 * @param {?string} args.newLaborId - labor id the item points at after (if any)
 * @param {boolean} args.mirrorToPickup - mirror ready deltas into status.pickup.*
 * @param {(fieldPath:string)=>number} args.getCurrent - reads the sale's current counter value
 * @returns {{saleChanges: Object<string,number>, laborDeltas: Object<string,number>}}
 */
export function buildSaleItemStatusUpdates({
  oldBucket,
  newBucket,
  oldQuantities,
  newQuantities,
  oldLaborId,
  newLaborId,
  mirrorToPickup,
  getCurrent,
}) {
  const oldC = bucketContributions(oldBucket, oldQuantities, {
    mirrorToPickup,
    hasLabor: Boolean(oldLaborId),
  });
  const newC = bucketContributions(newBucket, newQuantities, {
    mirrorToPickup,
    hasLabor: Boolean(newLaborId),
  });

  const saleChanges = {};
  // Net deltas for the sale-level INSTALL counters (status.install.*). These
  // are also written into `saleChanges` (against the item's sale) for the
  // normal same-sale case, but a cross-sale install caller needs the raw net
  // to re-apply them to the LABOR's sale instead — see UPDATE_SALE_ITEM_STATUS.
  const installDeltas = {};
  const fields = new Set([...Object.keys(oldC), ...Object.keys(newC)]);
  for (const field of fields) {
    const net = (newC[field] || 0) - (oldC[field] || 0);
    if (net === 0) continue;
    saleChanges[field] = Math.max(0, (getCurrent(field) || 0) + net);
    if (field.startsWith("status.install.")) {
      installDeltas[field] = net;
    }
  }

  // Per-labor status.quantityReady deltas. Only units feed a labor's ready
  // count. On a bucket move out of units (or a labor reassignment) the old
  // labor is decremented and the new labor incremented; on the common
  // same-bucket, same-labor path these collapse to a single net delta.
  const laborDeltas = {};
  const oldReady = oldQuantities.ready || 0;
  const newReady = newQuantities.ready || 0;
  if (oldBucket === "units" && oldLaborId && oldReady) {
    laborDeltas[oldLaborId] = (laborDeltas[oldLaborId] || 0) - oldReady;
  }
  if (newBucket === "units" && newLaborId && newReady) {
    laborDeltas[newLaborId] = (laborDeltas[newLaborId] || 0) + newReady;
  }
  for (const laborId of Object.keys(laborDeltas)) {
    if (!laborDeltas[laborId]) delete laborDeltas[laborId];
  }

  return { saleChanges, installDeltas, laborDeltas };
}

/**
 * Re-opens a fully delivered item so a damage replacement can be re-delivered.
 *
 * WHY THIS EXISTS. When critical damage is reported on an item that was ALREADY
 * delivered, nothing re-opens it: `functions/issues.js > updateCriticalDamage`
 * only runs while `quantityReady > quantityDelivered` ("QC checked but not yet
 * delivered") and has no branch for delivered stock. The reorder QC cannot
 * rescue it either — `computeQcReadyUpdate` clamps `newlyReady` by
 * `quantity - quantityReady`, which is 0 on a delivered item, so the
 * replacement adds no deliverable quantity. The item sits at
 * `quantityDelivered === quantityReady === quantity` with `fullyDelivered`,
 * which `Sale.hasDeliveryToSchedule` reads as done, so no delivery can be
 * scheduled and the replacement can never reach the customer.
 *
 * WHY IT IS EXPLICIT rather than automatic on the damage report: this reverses
 * recorded delivery history. That should be a deliberate act, not a side effect
 * of filing an issue.
 *
 * WHY IT TOUCHES EVENTS TOO. Re-opening the item document alone does not work.
 * Sale-level delivery counters are recomputed from the delivery EVENT's
 * `items` / `status.delivered` maps by
 * `functions/sales-helpers.js > computeDeliveryCountersFromEvents`, so while the
 * event still records the item as delivered a later Repair recomputes the
 * counters straight back and re-wedges the sale. Both sides have to move
 * together, which is why this is one action rather than two.
 */
export const REOPEN_ITEM_FOR_REDELIVERY = async (
  { dispatch, state },
  { projectID, saleID, itemID }
) => {
  const item = await dispatch("GET_SALE_ITEM", { projectID, saleID, itemID });
  if (!item) {
    throw "Not found";
  }

  const data = item.data;
  const quantity = data.quantity || 0;
  const oldDelivered = data.quantityDelivered || 0;
  const oldInstalled = data.quantityInstalled || 0;
  const oldReady = data.quantityReady || 0;
  const oldScheduled = data.quantityScheduled || 0;

  if (!oldDelivered && !data.fullyDelivered) {
    throw "reopenRedeliveryNotDelivered";
  }

  // `computeItemDeliveredFromLocations` floors quantityDelivered at
  // quantityInstalled, so re-opening an installed item would be silently undone
  // by the next repairSale. Refuse rather than write a state that does not
  // survive: the installed unit has to be un-installed first.
  if (oldInstalled > 0) {
    throw "reopenRedeliveryInstalled";
  }

  // Put the stock back where it physically was before it shipped — the most
  // recent snapshot that still held non-final entries. A snapshot whose latest
  // entries are all `type: "final"` is what marks an item delivered, so the
  // replacement needs a real warehouse position to return to. Inventing one
  // would be worse than refusing: receive the replacement through QC instead,
  // which is the path that creates warehouse locations to begin with.
  const history = normalizeItemLocationHistory(data.itemLocations || {});
  const hasStock = (entry) =>
    entry && entry.type !== "final" && (Number(entry.quantity) || 0) > 0;
  const priorKey = Object.keys(history)
    .sort()
    .reverse()
    .find((key) => (history[key] || []).some(hasStock));
  if (!priorKey) {
    throw "reopenRedeliveryNoPriorLocation";
  }
  const restored = (history[priorKey] || [])
    .filter(hasStock)
    .map((entry) => ({ ...entry }));

  // Keep quantityReady equal to what the restored locations actually hold.
  // computeQcReadyUpdate bounds a later QC by BOTH `quantity - quantityReady`
  // and `quantity - existingTotal`, so a quantityReady that overstates the
  // locations would block the reorder QC from topping the item back up.
  const restoredTotal = Math.min(
    restored.reduce((sum, entry) => sum + (Number(entry.quantity) || 0), 0),
    quantity
  );

  const now = new Date();
  await updateDoc(doc(db, item.path), {
    quantityReady: restoredTotal,
    quantityScheduled: 0,
    quantityDelivered: 0,
    fullyDelivered: false,
    fullyScheduled: false,
    [`itemLocations.${now.getTime()}`]: restored,
    modified: now,
    modifiedBy: state.user.id,
  });

  await dispatch("UPDATE_SALE_ITEM_STATUS", {
    projectID,
    saleID,
    item,
    oldDelivered,
    oldInstalled,
    oldQuantity: quantity,
    oldQuantityReady: oldReady,
    oldQuantityScheduled: oldScheduled,
    newDelivered: 0,
    newInstalled: 0,
    newQuantity: quantity,
    newQuantityReady: restoredTotal,
    newQuantityScheduled: 0,
  });

  // Drop the item from every delivery event that still counts it, so a later
  // Repair recomputes the sale counters to match the re-opened item instead of
  // restoring them from the event.
  const sale = state.sales[projectID + saleID];
  const eventIDs = sale?.data?.status?.delivery?.events || [];
  for (const eventID of eventIDs) {
    const event = state.events[eventID];
    if (!event) continue;
    const changes = {};
    if (event.data?.items && itemID in event.data.items) {
      changes[`items.${itemID}`] = deleteField();
    }
    if (
      event.data?.status?.delivered &&
      itemID in event.data.status.delivered
    ) {
      changes[`status.delivered.${itemID}`] = deleteField();
    }
    if (Object.keys(changes).length) {
      await dispatch("UPDATE_EVENT", { id: eventID, changes });
    }
  }

  return item;
};

export const UPDATE_SALE_ITEM_STATUS = async (
  { dispatch, state },
  {
    projectID,
    saleID,
    item,
    newItem,
    oldDelivered,
    oldInstalled,
    oldQuantity,
    oldQuantityReady,
    oldQuantityScheduled,
    newDelivered,
    newInstalled,
    newQuantity,
    newQuantityReady,
    newQuantityScheduled,
  }
) => {
  const sale = state.sales[projectID + saleID];
  if (!sale) {
    return;
  }

  // `item` carries the OLD classification (bucket + labor); `newItem` carries
  // the NEW one. Callers that don't reclassify (relocate, issue adjustments,
  // delete) omit `newItem`, so it defaults to `item` and no bucket move occurs.
  const oldItem = item;
  const nextItem = newItem || item;

  const oldBucket = classifyItemBucket(oldItem);
  const newBucket = classifyItemBucket(nextItem);

  // quantityDelivered is stored as max(delivered, installed) on save, so
  // normalize the incoming "new" delivered the same way.
  const normalizedNewDelivered = Math.max(newDelivered || 0, newInstalled || 0);

  const oldQuantities = {
    delivered: oldDelivered || 0,
    installed: oldInstalled || 0,
    quantity: oldQuantity || 0,
    ready: oldQuantityReady || 0,
    scheduled: oldQuantityScheduled || 0,
  };
  const newQuantities = {
    delivered: normalizedNewDelivered,
    installed: newInstalled || 0,
    quantity: newQuantity || 0,
    ready: newQuantityReady || 0,
    scheduled: newQuantityScheduled || 0,
  };

  // For CPU sales whose pickup address is a company warehouse, items are
  // produced directly into the pickup location and no delivery-to-pickup
  // event ever fires — so `status.pickup.*Ready` would never be populated
  // without this mirror. We bump it alongside `status.delivery.*Ready` so
  // `pickup.ready` is authoritative rather than requiring consumer-site
  // fallbacks. (Screens/hardware buckets get the same treatment.)
  const mirrorToPickup = isPickupAddressAtWarehouse(sale);

  const { saleChanges, installDeltas, laborDeltas } =
    buildSaleItemStatusUpdates({
      oldBucket,
      newBucket,
      oldQuantities,
      newQuantities,
      oldLaborId: oldItem?.data?.labor || null,
      newLaborId: nextItem?.data?.labor || null,
      mirrorToPickup,
      getCurrent: (fieldPath) => get(sale.data, fieldPath) || 0,
    });

  // Cross-sale install: the installing labor may live on a DIFFERENT sale in
  // this project than the item (e.g. sale B's labor installing sale A's
  // material), so route each update to the labor's own sale rather than the
  // item's. The labor's sale is read from the loaded labor when available,
  // else from the item's recorded `laborSaleID`, else it is the same sale (the
  // normal case).
  const laborSaleIdFor = (laborId) => {
    const loaded = state.saleLabors[laborId];
    if (loaded && loaded.saleID) return loaded.saleID;
    if (oldItem?.data?.labor === laborId && oldItem.data.laborSaleID) {
      return oldItem.data.laborSaleID;
    }
    if (nextItem?.data?.labor === laborId && nextItem.data.laborSaleID) {
      return nextItem.data.laborSaleID;
    }
    return saleID;
  };

  // Per-labor status.quantityReady increments, routed to the labor's sale.
  for (const [laborId, delta] of Object.entries(laborDeltas)) {
    await updateDoc(
      doc(db, SaleLabor.getPath(projectID, laborSaleIdFor(laborId), laborId)),
      {
        "status.quantityReady": increment(delta),
        modified: new Date(),
        modifiedBy: state.user.id,
      }
    );
  }

  // The sale-level INSTALL counters (status.install.*) belong to the labor's
  // sale, not the item's. When they diverge (cross-sale install), strip those
  // fields from the item-sale update and re-apply them against the labor
  // sale's own current values; otherwise they stay in `saleChanges` and go out
  // with the item's sale (the normal same-sale path, unchanged).
  const installLaborId = nextItem?.data?.labor || oldItem?.data?.labor || null;
  const installSaleID = installLaborId
    ? laborSaleIdFor(installLaborId)
    : saleID;
  if (installSaleID !== saleID && Object.keys(installDeltas).length > 0) {
    for (const field of Object.keys(installDeltas)) {
      delete saleChanges[field];
    }
    const installSale =
      state.sales[projectID + installSaleID] ||
      (await dispatch("GET_SALE", { projectID, saleID: installSaleID }));
    const installData = installSale && installSale.data ? installSale.data : {};
    const installChanges = {};
    for (const [field, net] of Object.entries(installDeltas)) {
      installChanges[field] = Math.max(0, (get(installData, field) || 0) + net);
    }
    await dispatch("UPDATE_SALE_STATUS", {
      projectID,
      saleID: installSaleID,
      changes: installChanges,
    });
  }

  if (Object.keys(saleChanges).length > 0) {
    return dispatch("UPDATE_SALE_STATUS", {
      projectID,
      saleID,
      changes: saleChanges,
    });
  }
};

export const RELOCATE_SALE_ITEM = (
  { state },
  { projectID, saleID, itemID, locations }
) => {
  return setDoc(
    doc(db, SaleItem.getPath(projectID, saleID, itemID)),
    {
      modified: new Date(),
      modifiedBy: state.user.id,
      itemLocations: {
        [new Date().getTime()]: locations.filter((l) => l.quantity > 0),
      },
    },
    { merge: true }
  );
};

export const UPDATE_SALE_ITEM = (
  { state },
  { projectID, saleID, itemID, changes }
) => {
  changes = { ...changes, modified: new Date(), modifiedBy: state.user.id };
  return setDoc(doc(db, SaleItem.getPath(projectID, saleID, itemID)), changes, {
    merge: true,
  });
};

// Sale Labor

export const GET_SALE_LABOR = async (
  { commit, state },
  { projectID, saleID, laborID }
) => {
  const path = SaleLabor.getPath(projectID, saleID, laborID);
  if (laborID in state.saleLabors) {
    return state.saleLabors[laborID];
  }
  const snapshot = await getDoc(doc(db, path).withConverter(SaleLabor));
  if (!snapshot.exists()) {
    throw "Not found";
  }
  const saleLabor = snapshot.data();
  commit("SET", { stateKey: "saleLabors", doc: saleLabor });
  return saleLabor;
};

export const GET_SALE_LABORS = async (
  { commit, state },
  { projectID, saleID, laborIDs }
) => {
  // Accumulate fetched docs and commit them in a single SET_MANY instead of
  // one SET per item. Each SET mutates the reactive `saleLabors` map and forces
  // every consumer (e.g. an event's `labors` getter, read per render) to
  // recompute; batching collapses N re-render passes into one. `getDoc` (not a
  // transaction) is used so reads can still be served from the offline cache.
  const validLaborIDs = (laborIDs || []).filter((id) => id);
  const docs = [];
  await Promise.all(
    validLaborIDs.map((laborID) => {
      if (laborID in state.saleLabors) {
        return undefined;
      }
      return getDoc(
        doc(db, SaleLabor.getPath(projectID, saleID, laborID)).withConverter(
          SaleLabor
        )
      ).then((snapshot) => {
        const data = snapshot.data();
        if (data) {
          docs.push({ stateID: snapshot.id, doc: data });
        }
      });
    })
  );

  if (docs.length) {
    commit("SET_MANY", { stateKey: "saleLabors", docs });
  }

  return validLaborIDs.map((id) => state.saleLabors[id]);
};

export const LISTEN_SALE_LABOR = (
  { dispatch },
  { projectID, saleID, laborID, error, next, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: SaleLabor,
    path: SaleLabor.getPath(projectID, saleID, laborID),
    stateKey: "saleLabors",
    id: laborID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_SALE_LABORS = (
  { dispatch },
  { projectID, saleID, conditions, listenerPath, error, next, unlisten }
) => {
  const path =
    projectID && saleID ? SaleLabor.getPath(projectID, saleID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: SaleLabor,
    stateKey: "saleLabors",
    path,
    colGroup: conditions || !path ? "labor" : null,
    listenerPath,
    conditions,
    next,
    error,
    unlisten,
  });
};

export const UPDATE_SALE_LABOR = (
  { state },
  { projectID, saleID, laborID, changes }
) => {
  changes = { ...changes, modified: new Date(), modifiedBy: state.user.id };
  return updateDoc(
    doc(db, SaleLabor.getPath(projectID, saleID, laborID)),
    changes,
    { merge: true }
  );
};

export const UPDATE_SALE_LABOR_LOADED = (
  { state },
  { projectID, saleID, laborID, key, count }
) =>
  setLoadedCount(
    doc(db, SaleLabor.getPath(projectID, saleID, laborID)),
    key,
    count,
    state.user.id
  );

export const SALE_LABOR_ITEM_COMPLETE = (
  context,
  { projectID, saleID, laborID, itemID, quantity }
) => {
  return call("saleLaborItemComplete", {
    projectID,
    saleID,
    laborID,
    itemID,
    quantity,
  });
};

// Delivery/Labor Callable Functions

export const BEGIN_LOADING_TRUCK = (context, params) => {
  return call("beginLoadingTruck", params);
};

export const CANCEL_LOADING_TRUCK = (context, params) => {
  return call("cancelLoadingTruck", params);
};

export const END_LOADING_TRUCK = (context, params) => {
  return call("endLoadingTruck", params);
};

export const UNLOAD_TRUCK = (context, params) => {
  return call("unloadTruck", params);
};

export const BEGIN_TRANSIT = (context, params) => {
  return call("beginTransit", params);
};

export const END_TRANSIT = (context, params) => {
  return call("endTransit", params);
};

/**
 * Build sale status changes for unscheduling a delivery's items.
 * Used when completing temporary deliveries or canceling deliveries.
 * @param {Object} sale - The sale object from state
 * @param {Object} delivery - The delivery event object
 * @param {boolean} isToPickupLocation - Whether the delivery is to the pickup location
 * @returns {Object} Changes to apply via UPDATE_SALE_STATUS
 */
export function buildUnscheduleDeliveryChanges(
  sale,
  delivery,
  isToPickupLocation = false
) {
  const changes = {};
  if (delivery.unitCount > 0) {
    changes["status.delivery.scheduled"] =
      sale.data.status.delivery.scheduled - delivery.unitCount;
  }
  if (delivery.screenCount > 0) {
    changes["status.delivery.screensScheduled"] =
      sale.data.status.delivery.screensScheduled - delivery.screenCount;
  }
  if (delivery.hardwareCount > 0) {
    changes["status.delivery.hardwareScheduled"] =
      sale.data.status.delivery.hardwareScheduled - delivery.hardwareCount;
  }
  if (delivery.accessoryCount > 0) {
    changes["status.delivery.accessoriesScheduled"] = Math.max(
      0,
      (sale.data.status.delivery.accessoriesScheduled || 0) -
        delivery.accessoryCount
    );
  }

  // For pickup sales, update pickup.ready when items arrive at pickup location
  if (isToPickupLocation) {
    const pickup = sale.data.status.pickup || {};
    if (delivery.unitCount > 0) {
      changes["status.pickup.ready"] = (pickup.ready || 0) + delivery.unitCount;
    }
    if (delivery.screenCount > 0) {
      changes["status.pickup.screensReady"] =
        (pickup.screensReady || 0) + delivery.screenCount;
    }
    if (delivery.hardwareCount > 0) {
      changes["status.pickup.hardwareReady"] =
        (pickup.hardwareReady || 0) + delivery.hardwareCount;
    }
    if (delivery.accessoryCount > 0) {
      changes["status.pickup.accessoriesReady"] =
        (pickup.accessoriesReady || 0) + delivery.accessoryCount;
    }
  }

  return changes;
}

/**
 * Check if a delivery destination matches the sale's pickup location.
 * @param {Object} delivery - The delivery event object
 * @param {Object} sale - The sale object
 * @returns {boolean}
 */
function isDeliveryToPickupLocation(delivery, sale) {
  if (!sale.data.shipTo?.pickup) return false;
  const pickupAddress = sale.data.shipTo?.address;
  // delivery.location is a ShipTo or Address object from the getter
  // delivery.data.location is the raw data
  const locationData = delivery.data?.location;
  if (!locationData || !pickupAddress) return false;
  // locationData can be { type, address } (ShipTo-like) or just address fields
  const deliveryAddress = locationData.address || locationData;
  if (!deliveryAddress) return false;
  return new Address(pickupAddress).isEqual(new Address(deliveryAddress));
}

/**
 * Complete a temporary delivery to a warehouse destination.
 * This is an alternative to COMPLETE_DELIVERY for when items are unloaded
 * at a warehouse instead of a customer location.
 */
export const COMPLETE_WAREHOUSE_DELIVERY = async (
  { dispatch, state },
  { projectID, saleID, deliveryID, destinations }
) => {
  // Capture loaded quantities and sale/delivery refs BEFORE calling unloadTruck.
  // The Firestore real-time listener may fire during the await and update
  // state.events[deliveryID] so that loaded becomes {} by the time the call
  // returns — reading it afterwards would always give units=0 and fail to
  // increment pickup.ready for CPU-at-location sales.
  const deliveryBefore = state.events[deliveryID];
  const sale = state.sales[projectID + saleID];
  let preCallUnits = 0;
  let preCallScreens = 0;
  let preCallHardware = 0;
  let preCallAccessories = 0;
  // A warehouse-destination completion is always temporary by nature (the
  // items are unloaded into one of our warehouses, not delivered to the
  // customer), so the work below must run regardless of the event's
  // `isTemporary` flag. Gating on `isTemporary` skipped the sale-status
  // unschedule whenever the warehouse-destination toggle wasn't set, leaving
  // `status.delivery.scheduled` pinned at full and hiding the items from the
  // delivery selector (sale 01000031A regression). Mirrors the backend
  // `unloadTruck` markDelivered branch, which always unschedules.
  if (deliveryBefore && sale) {
    const loaded = deliveryBefore.status?.loaded || {};
    // `loaded` is keyed by `${itemID}-${locationID}` (the source warehouse
    // zone or vehicle the units were loaded from), so split off the location
    // suffix before looking up the sale item. Treating the full composite key
    // as the item ID returned undefined and miscounted the screens/hardware
    // breakdown used for pickup-location delivery counters.
    // Items belonging to additional sales are excluded here and processed
    // separately below.
    const additionalItemIDs = new Set();
    Object.values(deliveryBefore.data?.additionalSales || {}).forEach(
      (saleData) => {
        Object.keys(saleData.items || {}).forEach((id) =>
          additionalItemIDs.add(id)
        );
      }
    );
    Object.entries(loaded).forEach(([key, quantity]) => {
      const itemID = key.split("-")[0];
      if (additionalItemIDs.has(itemID)) return;
      const item = state.saleItems[itemID];
      // Child accessories feed their own counters and must NOT land in
      // `units` — `delivery.complete` is measured against the quote-line
      // based `total.items`, which excludes them.
      if (item?.data?.parentID) {
        preCallAccessories += quantity;
        return;
      }
      preCallUnits += quantity;
      if (item?.data?.isScreen) preCallScreens += quantity;
      if (item?.data?.isHardware) preCallHardware += quantity;
    });
  }

  // Call unloadTruck with markDelivered to complete the delivery
  await call("unloadTruck", {
    eventID: deliveryID,
    destinations,
    markDelivered: true,
  });

  // Update sale status. Runs for every warehouse-destination completion (see
  // the note above on why `isTemporary` is not gated here).
  const delivery = deliveryBefore;
  if (delivery && sale) {
    const isToPickup = isDeliveryToPickupLocation(delivery, sale);
    const additionalSales = delivery?.data?.additionalSales || {};

    // Use the quantities captured before the call; by the time unloadTruck
    // resolves the Firestore listener may have already cleared the loaded map.
    const units = preCallUnits;
    const screens = preCallScreens;
    const hardware = preCallHardware;
    const accessories = preCallAccessories;

    const changes = buildDeliveryCompletionChanges(sale, delivery, {
      isToPickupLocation: isToPickup,
      totalDelivered: { units, screens, hardware, accessories },
    });
    if (Object.keys(changes).length > 0) {
      await dispatch("UPDATE_SALE_STATUS", {
        projectID,
        saleID,
        changes,
      });
    }

    // Process additional sales
    for (const [stateID, saleData] of Object.entries(additionalSales)) {
      const addProjectID = stateID.slice(0, -1);
      const addSaleID = stateID.slice(-1);
      const addSale = state.sales[stateID];
      if (!addSale) continue;

      const addItems = saleData.items || {};
      let addUnits = 0;
      let addScreens = 0;
      let addHardware = 0;
      let addAccessories = 0;
      Object.entries(addItems).forEach(([itemID, qty]) => {
        const item = state.saleItems[itemID];
        if (item) {
          if (item.data.parentID) {
            addAccessories += qty;
          } else if (item.isScreens && !item.data.line) {
            addScreens += qty;
          } else if (item.isHardware && !item.data.line) {
            addHardware += qty;
          } else {
            addUnits += qty;
          }
        }
      });

      const addDeliveryLike = {
        unitCount: addUnits,
        screenCount: addScreens,
        hardwareCount: addHardware,
        accessoryCount: addAccessories,
      };
      const addIsToPickup = isDeliveryToPickupLocation(delivery, addSale);

      const addChanges = buildDeliveryCompletionChanges(
        addSale,
        addDeliveryLike,
        {
          isTemporary: true,
          isToPickupLocation: addIsToPickup,
          totalDelivered: {
            units: addUnits,
            screens: addScreens,
            hardware: addHardware,
            accessories: addAccessories,
          },
        }
      );

      if (Object.keys(addChanges).length > 0) {
        await dispatch("UPDATE_SALE_STATUS", {
          projectID: addProjectID,
          saleID: addSaleID,
          changes: addChanges,
        });
      }
    }
  }
};

/**
 * Build sale status changes for completing a delivery.
 * @param {Object} sale - The sale object from state
 * @param {Object} delivery - The delivery event object
 * @param {Object} options - Options including isToPickupLocation, isTemporary, totalDelivered
 * @returns {Object} Changes to apply via UPDATE_SALE_STATUS
 */
export function buildDeliveryCompletionChanges(sale, delivery, options = {}) {
  const { isToPickupLocation = false, isTemporary, totalDelivered } = options;
  // Default to temporary, but if isToPickupLocation is true without explicit isTemporary, treat as non-temporary
  const isTemp = isTemporary !== undefined ? isTemporary : !isToPickupLocation;
  const changes = {};

  if (isTemp) {
    // Temporary delivery - unschedule all items
    if (delivery.unitCount > 0) {
      changes["status.delivery.scheduled"] =
        sale.data.status.delivery.scheduled - delivery.unitCount;
    }
    if (delivery.screenCount > 0) {
      changes["status.delivery.screensScheduled"] =
        sale.data.status.delivery.screensScheduled - delivery.screenCount;
    }
    if (delivery.hardwareCount > 0) {
      changes["status.delivery.hardwareScheduled"] =
        sale.data.status.delivery.hardwareScheduled - delivery.hardwareCount;
    }
    if (delivery.accessoryCount > 0) {
      changes["status.delivery.accessoriesScheduled"] = Math.max(
        0,
        (sale.data.status.delivery.accessoriesScheduled || 0) -
          delivery.accessoryCount
      );
    }

    // If to pickup location, increment pickup.ready
    if (isToPickupLocation) {
      const pickup = sale.data.status.pickup || {};
      if (delivery.unitCount > 0) {
        changes["status.pickup.ready"] =
          (pickup.ready || 0) + delivery.unitCount;
      }
      if (delivery.screenCount > 0) {
        changes["status.pickup.screensReady"] =
          (pickup.screensReady || 0) + delivery.screenCount;
      }
      if (delivery.hardwareCount > 0) {
        changes["status.pickup.hardwareReady"] =
          (pickup.hardwareReady || 0) + delivery.hardwareCount;
      }
      if (delivery.accessoryCount > 0) {
        changes["status.pickup.accessoriesReady"] =
          (pickup.accessoriesReady || 0) + delivery.accessoryCount;
      }
    } else if (sale.data.shipTo?.pickup) {
      // Temp delivery to a non-pickup destination: items that were at the
      // pickup warehouse are now elsewhere, so decrement pickup.ready to
      // prevent a stale CPU button from showing (sale 04300131A).
      const pickup = sale.data.status.pickup || {};
      const deliveredUnits = totalDelivered?.units ?? delivery.unitCount;
      const deliveredScreens = totalDelivered?.screens ?? delivery.screenCount;
      const deliveredHardware =
        totalDelivered?.hardware ?? delivery.hardwareCount;
      const deliveredAccessories =
        totalDelivered?.accessories ?? delivery.accessoryCount;
      // Floor at the `complete` counterparts: `pickup.ready` is cumulative,
      // so already-picked-up units must stay counted (ready >= complete).
      if (deliveredUnits > 0) {
        changes["status.pickup.ready"] = Math.max(
          pickup.complete || 0,
          (pickup.ready || 0) - deliveredUnits
        );
      }
      if (deliveredScreens > 0) {
        changes["status.pickup.screensReady"] = Math.max(
          pickup.screensComplete || 0,
          (pickup.screensReady || 0) - deliveredScreens
        );
      }
      if (deliveredHardware > 0) {
        changes["status.pickup.hardwareReady"] = Math.max(
          pickup.hardwareComplete || 0,
          (pickup.hardwareReady || 0) - deliveredHardware
        );
      }
      if (deliveredAccessories > 0) {
        changes["status.pickup.accessoriesReady"] = Math.max(
          pickup.accessoriesComplete || 0,
          (pickup.accessoriesReady || 0) - deliveredAccessories
        );
      }
    }
  } else {
    // Regular delivery - increment complete counters
    const deliveredUnits = totalDelivered?.units ?? delivery.unitCount;
    const deliveredScreens = totalDelivered?.screens ?? delivery.screenCount;
    const deliveredHardware =
      totalDelivered?.hardware ?? delivery.hardwareCount;
    const deliveredAccessories =
      totalDelivered?.accessories ?? delivery.accessoryCount;

    if (deliveredUnits > 0) {
      changes["status.delivery.complete"] =
        (sale.data.status.delivery.complete || 0) + deliveredUnits;
    }
    if (deliveredScreens > 0) {
      changes["status.delivery.screensComplete"] =
        (sale.data.status.delivery.screensComplete || 0) + deliveredScreens;
    }
    if (deliveredHardware > 0) {
      changes["status.delivery.hardwareComplete"] =
        (sale.data.status.delivery.hardwareComplete || 0) + deliveredHardware;
    }
    if (deliveredAccessories > 0) {
      changes["status.delivery.accessoriesComplete"] =
        (sale.data.status.delivery.accessoriesComplete || 0) +
        deliveredAccessories;
    }

    // If to pickup location, also update pickup.ready
    if (isToPickupLocation) {
      const pickup = sale.data.status.pickup || {};
      if (deliveredUnits > 0) {
        changes["status.pickup.ready"] = (pickup.ready || 0) + deliveredUnits;
      }
      if (deliveredScreens > 0) {
        changes["status.pickup.screensReady"] =
          (pickup.screensReady || 0) + deliveredScreens;
      }
      if (deliveredHardware > 0) {
        changes["status.pickup.hardwareReady"] =
          (pickup.hardwareReady || 0) + deliveredHardware;
      }
      if (deliveredAccessories > 0) {
        changes["status.pickup.accessoriesReady"] =
          (pickup.accessoriesReady || 0) + deliveredAccessories;
      }
    }

    // Mark undelivered items as unscheduled (if totalDelivered provided and less than delivery count)
    if (totalDelivered) {
      if (totalDelivered.units < delivery.unitCount) {
        changes["status.delivery.scheduled"] =
          sale.data.status.delivery.scheduled -
          (delivery.unitCount - totalDelivered.units);
      }
      if (totalDelivered.screens < delivery.screenCount) {
        changes["status.delivery.screensScheduled"] =
          sale.data.status.delivery.screensScheduled -
          (delivery.screenCount - totalDelivered.screens);
      }
      if (totalDelivered.hardware < delivery.hardwareCount) {
        changes["status.delivery.hardwareScheduled"] =
          sale.data.status.delivery.hardwareScheduled -
          (delivery.hardwareCount - totalDelivered.hardware);
      }
      if (
        (totalDelivered.accessories || 0) < (delivery.accessoryCount || 0) &&
        delivery.accessoryCount
      ) {
        changes["status.delivery.accessoriesScheduled"] = Math.max(
          0,
          (sale.data.status.delivery.accessoriesScheduled || 0) -
            (delivery.accessoryCount - (totalDelivered.accessories || 0))
        );
      }
    }
  }

  return changes;
}

/**
 * Build sale status changes that reverse a completed delivery.
 *
 * The inverse of the `saleChanges` COMPLETE_DELIVERY applies for a
 * non-temporary delivery: the `complete` buckets, plus the pickup mirror for
 * CPU sales. Those increments live client-side, which is why the
 * undoDeliveryCompletion cloud function cannot reverse them itself.
 *
 * `scheduled` is deliberately not restored. A full non-temporary completion
 * never touched it (COMPLETE_DELIVERY only decrements `scheduled` when some
 * scheduled items went undelivered), and where it did, the release was of items
 * that genuinely never went out. The undo lands the event back in `loaded`, and
 * the Reschedule / Unload paths that follow do their own scheduling bookkeeping.
 *
 * @param {Object} sale - The sale object from state
 * @param {Object} undone - Quantities reversed, bucketed by the cloud function
 *   ({ units, screens, hardware, accessories })
 * @param {Object} options
 * @param {boolean} options.mirrorPickup - Also walk back the pickup counters.
 *   Only the primary sale's completion mirrors delivered quantities into the
 *   pickup counters (inline in COMPLETE_DELIVERY); the additional sales of a
 *   combined delivery never got that, so their undo must not reverse it.
 * @returns {Object} Changes to apply via UPDATE_SALE_STATUS
 */
export function buildDeliveryUndoChanges(sale, undone = {}, options = {}) {
  const { mirrorPickup = true } = options;
  const changes = {};
  const delivery = sale.data.status.delivery || {};
  const units = Number(undone.units) || 0;
  const screens = Number(undone.screens) || 0;
  const hardware = Number(undone.hardware) || 0;
  const accessories = Number(undone.accessories) || 0;

  if (units > 0) {
    changes["status.delivery.complete"] = Math.max(
      0,
      (delivery.complete || 0) - units
    );
  }
  if (screens > 0) {
    changes["status.delivery.screensComplete"] = Math.max(
      0,
      (delivery.screensComplete || 0) - screens
    );
  }
  if (hardware > 0) {
    changes["status.delivery.hardwareComplete"] = Math.max(
      0,
      (delivery.hardwareComplete || 0) - hardware
    );
  }
  if (accessories > 0) {
    changes["status.delivery.accessoriesComplete"] = Math.max(
      0,
      (delivery.accessoriesComplete || 0) - accessories
    );
  }

  // CPU sales mirror delivered quantities into the pickup counters on
  // completion, so undo has to walk those back too or the sale keeps reading as
  // picked up. `pickup.scheduled` is left where it is: completion only ever
  // raised it to meet `complete` (Math.max), and lowering it again would
  // unschedule a pickup the undo has no opinion about.
  if (mirrorPickup && sale.data.shipTo?.pickup) {
    const pickup = sale.data.status.pickup || {};
    if (units > 0) {
      changes["status.pickup.complete"] = Math.max(
        0,
        (pickup.complete || 0) - units
      );
    }
    if (screens > 0) {
      changes["status.pickup.screensComplete"] = Math.max(
        0,
        (pickup.screensComplete || 0) - screens
      );
    }
    if (hardware > 0) {
      changes["status.pickup.hardwareComplete"] = Math.max(
        0,
        (pickup.hardwareComplete || 0) - hardware
      );
    }
    if (accessories > 0) {
      changes["status.pickup.accessoriesComplete"] = Math.max(
        0,
        (pickup.accessoriesComplete || 0) - accessories
      );
    }
  }

  return changes;
}

export const COMPLETE_DELIVERY = async (
  { dispatch, state },
  {
    projectID,
    saleID,
    deliveryID,
    signature,
    signerName,
    noSignature,
    proof,
    pdf,
    items,
    noNotify,
    isTemporary,
  }
) => {
  const folderPath = reportUploadFolder(
    `projects/${projectID}/sales/${saleID}/deliveries/${deliveryID}`
  );

  if (!isTemporary) {
    // Store proof
    proof = await dispatch("SAVE_DOCUMENTS", {
      media: proof,
      folderPath,
    });

    signature = await uploadSignature(folderPath, signature);

    // Store PDF
    pdf = await dispatch("SAVE_DOCUMENTS", {
      folderPath,
      isSingle: true,
      media: pdf,
    });
  }

  const delivery = state.events[deliveryID];
  const additionalSales = delivery?.data?.additionalSales || {};

  const totalDelivered = await call("completeDelivery", {
    projectID,
    saleID,
    deliveryID,
    signature,
    signerName,
    noSignature,
    proof,
    pdf,
    items,
    noNotify,
    additionalSales,
  });
  const sale = state.sales[projectID + saleID];
  const saleChanges = {
    [`status.delivery.complete`]:
      sale.data.status.delivery.complete + !isTemporary * totalDelivered.units,
    [`status.delivery.screensComplete`]:
      sale.data.status.delivery.screensComplete +
      !isTemporary * totalDelivered.screens,
    [`status.delivery.hardwareComplete`]:
      sale.data.status.delivery.hardwareComplete +
      !isTemporary * totalDelivered.hardware,
    [`status.delivery.accessoriesComplete`]:
      (sale.data.status.delivery.accessoriesComplete || 0) +
      !isTemporary * (totalDelivered.accessories || 0),
  };

  // Mark undelivered items as unscheduled,
  // or if temporary, mark all delivered items as unscheduled
  if (totalDelivered.units < delivery.unitCount || isTemporary) {
    saleChanges["status.delivery.scheduled"] =
      sale.status.delivery.scheduled -
      (delivery.unitCount - !isTemporary * totalDelivered.units);
  }
  if (totalDelivered.screens < delivery.screenCount || isTemporary) {
    saleChanges["status.delivery.screensScheduled"] =
      sale.status.delivery.screensScheduled -
      (delivery.screenCount - !isTemporary * totalDelivered.screens);
  }
  if (totalDelivered.hardware < delivery.hardwareCount || isTemporary) {
    saleChanges["status.delivery.hardwareScheduled"] =
      sale.status.delivery.hardwareScheduled -
      (delivery.hardwareCount - !isTemporary * totalDelivered.hardware);
  }
  if (
    delivery.accessoryCount &&
    ((totalDelivered.accessories || 0) < delivery.accessoryCount || isTemporary)
  ) {
    saleChanges["status.delivery.accessoriesScheduled"] = Math.max(
      0,
      (sale.status.delivery.accessoriesScheduled || 0) -
        (delivery.accessoryCount -
          !isTemporary * (totalDelivered.accessories || 0))
    );
  }

  // For CPU sales whose items went out on a final delivery instead of a
  // customer pickup, mirror the delivered quantities into the pickup
  // counters — for pickup sales those are the gauges computeSaleStatus and
  // the pickup widgets read (the mirror of completePickup routing ad-hoc
  // pickups on jobsite sales into the delivery counters). Floor each
  // scheduled counter at its new complete so the scheduled >= complete
  // invariant holds and the sale leaves the pickup-scheduling widgets
  // (hasPickupToSchedule checks screensScheduled < totalScreens etc.).
  // Without this the sale stays in Pickups to Schedule forever
  // (sale 04300072A).
  if (!isTemporary && sale.data.shipTo?.pickup) {
    const pickup = sale.data.status.pickup || {};
    if (totalDelivered.units > 0) {
      const complete = (pickup.complete || 0) + totalDelivered.units;
      saleChanges["status.pickup.complete"] = complete;
      saleChanges["status.pickup.scheduled"] = Math.max(
        pickup.scheduled || 0,
        complete
      );
    }
    if (totalDelivered.screens > 0) {
      const screensComplete =
        (pickup.screensComplete || 0) + totalDelivered.screens;
      saleChanges["status.pickup.screensComplete"] = screensComplete;
      saleChanges["status.pickup.screensScheduled"] = Math.max(
        pickup.screensScheduled || 0,
        screensComplete
      );
    }
    if (totalDelivered.hardware > 0) {
      const hardwareComplete =
        (pickup.hardwareComplete || 0) + totalDelivered.hardware;
      saleChanges["status.pickup.hardwareComplete"] = hardwareComplete;
      saleChanges["status.pickup.hardwareScheduled"] = Math.max(
        pickup.hardwareScheduled || 0,
        hardwareComplete
      );
    }
    if ((totalDelivered.accessories || 0) > 0) {
      const accessoriesComplete =
        (pickup.accessoriesComplete || 0) + totalDelivered.accessories;
      saleChanges["status.pickup.accessoriesComplete"] = accessoriesComplete;
      saleChanges["status.pickup.accessoriesScheduled"] = Math.max(
        pickup.accessoriesScheduled || 0,
        accessoriesComplete
      );
    }
  }

  // For CPU sales, decrement pickup.ready when items are delivered to a
  // non-pickup temporary destination. Items that were at the pickup warehouse
  // are now at the temp location, so they're no longer available for pickup.
  // Without this, pickup.ready stays stale and the CPU button keeps showing
  // even though no items can be selected (getItemQuantityAtPickupSource = 0),
  // while the "delivery to pickup" button stays hidden because
  // hasDeliveryToSchedule computes unitsAway = 0 (sale 04300131A).
  if (isTemporary && sale.data.shipTo?.pickup) {
    const isToPickup = isDeliveryToPickupLocation(delivery, sale);
    if (!isToPickup) {
      const pickup = sale.data.status.pickup || {};
      // Floor each counter at its `complete` counterpart: `pickup.ready` is
      // cumulative, so units the customer already picked up must stay
      // counted (the progress indicators require ready >= complete).
      if (totalDelivered.units > 0) {
        saleChanges["status.pickup.ready"] = Math.max(
          pickup.complete || 0,
          (pickup.ready || 0) - totalDelivered.units
        );
      }
      if (totalDelivered.screens > 0) {
        saleChanges["status.pickup.screensReady"] = Math.max(
          pickup.screensComplete || 0,
          (pickup.screensReady || 0) - totalDelivered.screens
        );
      }
      if (totalDelivered.hardware > 0) {
        saleChanges["status.pickup.hardwareReady"] = Math.max(
          pickup.hardwareComplete || 0,
          (pickup.hardwareReady || 0) - totalDelivered.hardware
        );
      }
      if ((totalDelivered.accessories || 0) > 0) {
        saleChanges["status.pickup.accessoriesReady"] = Math.max(
          pickup.accessoriesComplete || 0,
          (pickup.accessoriesReady || 0) - totalDelivered.accessories
        );
      }
    }
  }

  await dispatch("UPDATE_SALE_STATUS", {
    projectID,
    saleID,
    changes: saleChanges,
  });

  // Process additional sales
  for (const [stateID, saleData] of Object.entries(additionalSales)) {
    const addProjectID = stateID.slice(0, -1);
    const addSaleID = stateID.slice(-1);
    let addSale = state.sales[stateID];
    if (!addSale) {
      // The passenger sale is not necessarily in the store (the report dialog
      // listens to the primary sale only). Skipping it silently left that
      // sale's counters untouched; fetch it instead.
      try {
        addSale = await dispatch("GET_SALE", {
          projectID: addProjectID,
          saleID: addSaleID,
        });
      } catch (error) {
        addSale = null;
      }
    }
    if (!addSale) continue;

    // What this sale had scheduled on the event and what actually came off
    // the truck, bucketed the way its counters are. The cloud function reports
    // both per passenger sale (`totalDelivered.additional[stateID]`); its
    // delivered figures come from the truck's loaded map, so a passenger item
    // nobody checked off at loading counts as delivered 0 and is released
    // from `scheduled` below — the previous assumption that a full completion
    // delivered every scheduled passenger item credited sales 05400045B/C
    // with units still sitting in the warehouse.
    const serverTotals =
      totalDelivered && totalDelivered.additional
        ? totalDelivered.additional[stateID]
        : null;
    let addDeliveryLike;
    let addDelivered;
    if (serverTotals) {
      const scheduled = serverTotals.scheduled || {};
      addDeliveryLike = {
        unitCount: scheduled.units || 0,
        screenCount: scheduled.screens || 0,
        hardwareCount: scheduled.hardware || 0,
        accessoryCount: scheduled.accessories || 0,
      };
      addDelivered = {
        units: serverTotals.units || 0,
        screens: serverTotals.screens || 0,
        hardware: serverTotals.hardware || 0,
        accessories: serverTotals.accessories || 0,
      };
    } else {
      // Fallback for a cloud function that predates the per-sale totals:
      // classify from the store. `items` is only provided for partial
      // completions; when it is, an item missing from the map was NOT
      // delivered. For full completions (`items` is null) assume the
      // scheduled quantity was delivered.
      const scheduledItems = saleData.items || {};
      let addUnits = 0;
      let addScreens = 0;
      let addHardware = 0;
      let addAccessories = 0;
      Object.entries(scheduledItems).forEach(([itemID, scheduledQty]) => {
        const qty = items ? Number(items[itemID]) || 0 : scheduledQty;
        const item = state.saleItems[itemID];
        if (item) {
          if (item.data.parentID) {
            addAccessories += qty;
          } else if (item.isScreens && !item.data.line) {
            addScreens += qty;
          } else if (item.isHardware && !item.data.line) {
            addHardware += qty;
          } else {
            addUnits += qty;
          }
        }
      });
      addDeliveryLike = {
        unitCount: addUnits,
        screenCount: addScreens,
        hardwareCount: addHardware,
        accessoryCount: addAccessories,
      };
      addDelivered = {
        units: addUnits,
        screens: addScreens,
        hardware: addHardware,
        accessories: addAccessories,
      };
    }
    const addIsToPickup =
      isTemporary && isDeliveryToPickupLocation(delivery, addSale);

    const addChanges = buildDeliveryCompletionChanges(
      addSale,
      addDeliveryLike,
      {
        isTemporary,
        isToPickupLocation: addIsToPickup,
        totalDelivered: addDelivered,
      }
    );

    if (Object.keys(addChanges).length > 0) {
      await dispatch("UPDATE_SALE_STATUS", {
        projectID: addProjectID,
        saleID: addSaleID,
        changes: addChanges,
      });
    }
  }
};

// Reverse an accidentally-completed delivery back to the `loaded` phase.
//
// The cloud function owns the event and the items; the sale counters are
// reversed here because the completion incremented them here too
// (see COMPLETE_DELIVERY above). It returns the quantities it actually reversed,
// bucketed the same way completeDelivery buckets them, so the two halves cannot
// drift apart.
export const UNDO_DELIVERY_COMPLETION = async (
  { dispatch, state },
  { projectID, saleID, deliveryID }
) => {
  const { undone, additionalUndone, warnings, notes, invoiceSwap } = await call(
    "undoDeliveryCompletion",
    {
      deliveryID,
    }
  );

  const sale = state.sales[projectID + saleID];
  if (sale) {
    const changes = buildDeliveryUndoChanges(sale, undone);
    if (invoiceSwap) {
      // The sale's status.invoices map is client-maintained: drop the voided
      // replacement's entry and restore the original's (touchSale then
      // re-stamps the restored invoice's events).
      if (invoiceSwap.voided) {
        changes[`status.invoices.${invoiceSwap.voided}`] = deleteField();
      }
      changes[`status.invoices.${invoiceSwap.restored.id}.events`] =
        invoiceSwap.restored.events;
      changes[`status.invoices.${invoiceSwap.restored.id}.fullyCollected`] =
        !invoiceSwap.restored.balance;
    }
    if (Object.keys(changes).length > 0) {
      await dispatch("UPDATE_SALE_STATUS", {
        projectID,
        saleID,
        changes,
      });
    }
  }

  // Combined deliveries: reverse each additional sale's counters, exactly as
  // COMPLETE_DELIVERY incremented them per sale. The pickup mirror is
  // primary-only (completion applies it inline, not via
  // buildDeliveryCompletionChanges), so additional sales never reverse it.
  for (const [stateID, addUndone] of Object.entries(additionalUndone || {})) {
    const addSale = state.sales[stateID];
    if (!addSale) continue;
    const addChanges = buildDeliveryUndoChanges(addSale, addUndone, {
      mirrorPickup: false,
    });
    if (Object.keys(addChanges).length > 0) {
      await dispatch("UPDATE_SALE_STATUS", {
        projectID: stateID.slice(0, -1),
        saleID: stateID.slice(-1),
        changes: addChanges,
      });
    }
  }

  return { undone, warnings, notes };
};

export const COMPLETE_PICKUP = async (
  context,
  { projectID, saleID, pickupID, items, signature, signerName }
) => {
  // Store signature (same convention as COMPLETE_DELIVERY): the backend
  // records whatever string it receives, so upload the SVG data URI and
  // pass the download URL instead — a raw data URI would bloat the event
  // document. Without this, the signature captured on the Pickup page was
  // silently dropped and `status.signature` was always null (04100066A).
  if (signature) {
    signature = await uploadSignature(
      reportUploadFolder(
        `projects/${projectID}/sales/${saleID}/pickups/${pickupID}`
      ),
      signature
    );
  }

  // `completePickup` writes the sale's counters itself, in the right bucket
  // (`status.delivery.*` on a jobsite sale), and touchSale recomputes
  // `status.type` from them. The #888 merge brought back a client-side write
  // here that re-applied the completion to `status.pickup.*` from a stale
  // cached sale and decremented `pickup.scheduled`, which completion must
  // leave alone.
  return call("completePickup", {
    eventID: pickupID,
    items,
    signature,
    signerName,
  });
};

export const SUBMIT_SALE_LABOR_REPORT = async (
  { dispatch, state },
  {
    projectID,
    saleID,
    laborIDs,
    newHours,
    newLaborHours,
    completeLabors,
    signature,
    signerName,
    noSignature,
    pdf,
    noNotify,
  }
) => {
  const folderPath = reportUploadFolder(
    `projects/${projectID}/sales/${saleID}/labor/${laborIDs[0]}`
  );

  // Store signature
  if (signature) {
    signature = await uploadSignature(folderPath, signature);
  }

  // Store PDF
  if (pdf) {
    pdf = await dispatch("SAVE_DOCUMENTS", {
      folderPath,
      isSingle: true,
      media: pdf,
    });
  }

  const { totalNewlyDelivered, totalNewlyInstalled, totalNewlyComplete } =
    await call("laborReport", {
      projectID,
      saleID,
      laborIDs,
      newHours,
      newLaborHours,
      completeLabors,
      signature,
      signerName,
      noSignature,
      pdf,
      noNotify,
    });
  const sale = state.sales[projectID + saleID];
  const saleChanges = {
    "status.delivery.scheduled":
      sale.data.status.delivery.scheduled + totalNewlyDelivered,
    "status.delivery.complete":
      sale.data.status.delivery.complete + totalNewlyDelivered,
    "status.install.completeItems":
      sale.data.status.install.completeItems + totalNewlyInstalled,
    "status.install.completeLabors":
      sale.data.status.install.completeLabors + totalNewlyComplete,
    "status.install.completeHours":
      sale.data.status.install.completeHours + newHours,
  };
  return dispatch("UPDATE_SALE_STATUS", {
    projectID,
    saleID,
    changes: saleChanges,
  });
};

export const CLAIM_SALE_FOR_ORDERING = ({ state }, { projectID, saleID }) => {
  return updateDoc(doc(db, Sale.getPath(projectID, saleID)), {
    orderingClaimedBy: state.user.id,
    orderingClaimedAt: new Date(),
    modified: new Date(),
    modifiedBy: state.user.id,
  });
};

export const RELEASE_SALE_ORDERING_CLAIM = (
  { state },
  { projectID, saleID }
) => {
  return updateDoc(doc(db, Sale.getPath(projectID, saleID)), {
    orderingClaimedBy: null,
    orderingClaimedAt: null,
    modified: new Date(),
    modifiedBy: state.user.id,
  });
};

// NOTE: `total.additionalCost` is maintained server-side by the `touchExpense`
// Cloud Function (functions/expenses.js), which is the single source of truth.
// The former SAVE/DELETE/UPDATE_SALE_ADDITIONAL_COST actions were unused dead
// code that recomputed it client-side (excluding Labor / pending) and have been
// removed to avoid clobbering the authoritative value.
